import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  VStack,
  HStack,
  useColorModeValue,
  Input,
  Icon,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import api from '../api'; // adjust path as needed

// Move keyframes outside component to prevent recreation on every render
const glow = keyframes`
  0% { box-shadow: 0 0 15px rgba(59, 130, 246, 0.2); }
  50% { box-shadow: 0 0 20px rgba(16, 185, 129, 0.3); }
  100% { box-shadow: 0 0 15px rgba(59, 130, 246, 0.2); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.98; }
  100% { opacity: 1; }
`;

const Dashboard = () => {
  const [totalEarned, setTotalEarned] = useState(0);
  const [earnData, setEarnData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [lessProducts, setLessProducts] = useState([]);
  const [selectedText, setSelectedText] = useState(null);
  
  // Get current year's first day and current month's last day
  const getCurrentYearFirstDay = () => {
    const now = new Date();
    return `${now.getFullYear()}-01-01`;
  };
  
  const getCurrentMonthLastDay = () => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const year = lastDay.getFullYear();
    const month = String(lastDay.getMonth() + 1).padStart(2, '0');
    const day = String(lastDay.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [startDate, setStartDate] = useState(getCurrentYearFirstDay());
  const [endDate, setEndDate] = useState(getCurrentMonthLastDay());

  const fetchDashboardData = async () => {
    try {
      const totalEarnedRes = await api.get(`/total_earned/?start_date=${startDate}&end_date=${endDate}`);
      setTotalEarned(totalEarnedRes.data.total_earned || 0);
    } catch (error) {
      console.error('Failed to fetch total earned:', error);
      setTotalEarned(0); // Set default value on error
    }

    try {
      const earnDataRes = await api.get(`/earned_graphic_data/?start_date=${startDate}&end_date=${endDate}`);
      setEarnData(earnDataRes.data || []);
    } catch (error) {
      console.error('Failed to fetch earnings graph data:', error);
      setEarnData([]); // Set default value on error
    }

    try {
      const topProductsRes = await api.get('/statistics/top-sold-products/');
      setTopProducts(topProductsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch top sold products:', error);
      setTopProducts([]); // Set default value on error
    }

    try {
      const lessProductsRes = await api.get('/statistics/less-sold-products/');
      setLessProducts(lessProductsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch less sold products:', error);
      setLessProducts([]); // Set default value on error
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [startDate, endDate]); // Add dependencies to auto-update when dates change

  const getDateRangeLabels = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dates = [];
    let current = new Date(start);
    while (current <= end) {
      dates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const chartLabels = getDateRangeLabels();

  const chartDataMap = (earnData || []).reduce((acc, item) => {
    acc[item.date] = item.total_earned;
    return acc;
  }, {});

  const chartData = chartLabels.map(date => chartDataMap[date] || 0);

  // Memoize gradient creation for better performance
  const createChartGradient = React.useCallback((ctx) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, 'rgba(139, 92, 246, 0.8)');
    gradient.addColorStop(0.5, 'rgba(99, 102, 241, 0.6)');
    gradient.addColorStop(1, 'rgba(59, 130, 246, 0.4)');
    return gradient;
  }, []);

  const chartConfig = {
    labels: chartLabels,
    datasets: [
      {
        label: `Графік прибутку (з ${startDate} по ${endDate})`,
        data: chartData,
        backgroundColor: (context) => createChartGradient(context.chart.ctx),
        borderColor: 'rgba(139, 92, 246, 1)',
        borderWidth: 2,
        borderRadius: 6, // Reduced from 8
        borderSkipped: false,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: useColorModeValue('#2D3748', '#E2E8F0'),
          font: {
            size: 14,
            weight: '600',
          },
          padding: 20,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(139, 92, 246, 0.8)',
        borderWidth: 1,
        cornerRadius: 12,
        displayColors: false,
        callbacks: {
          label: function(context) {
            return `Прибуток: ${context.parsed.y.toLocaleString()} грн`;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: useColorModeValue('#4A5568', '#A0AEC0'),
          font: {
            size: 12,
          },
          maxTicksLimit: 10,
        },
        border: {
          display: false,
        },
      },
      y: {
        grid: {
          color: useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(255, 255, 255, 0.1)'),
          drawBorder: false,
        },
        ticks: {
          color: useColorModeValue('#4A5568', '#A0AEC0'),
          font: {
            size: 12,
          },
          callback: function(value) {
            return value.toLocaleString() + ' грн';
          },
        },
        border: {
          display: false,
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
    animation: {
      duration: 800, // Reduced from 2000ms
      easing: 'easeOutQuart', // Faster easing
    },
  };

  const statBoxBg = useColorModeValue('gray.100', 'gray.700');
  
  // Define color mode values outside of callbacks to avoid hook rule violations
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const listBg = useColorModeValue('gray.50', 'gray.700');
  const itemBg = useColorModeValue('white', 'gray.800');
  const itemBorderColor = useColorModeValue('gray.100', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const subtextColor = useColorModeValue('gray.500', 'gray.400');
  const greenHoverBorder = useColorModeValue('green.200', 'green.400');
  const orangeHoverBorder = useColorModeValue('orange.200', 'orange.400');
  const greenTextColor = useColorModeValue('green.600', 'green.400');
  const orangeTextColor = useColorModeValue('orange.600', 'orange.400');
  const blueHoverBorder = useColorModeValue('blue.200', 'blue.400');

  return (
    <Box position="relative" minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')}>
      <Box position="relative" zIndex={1} p={{ base: 2, md: 8 }}>
        {/* Date Filter Section - Modern Card Style */}
        <Box 
          position="relative"
          bg={cardBg} 
          p={{ base: 3, md: 6 }}
          borderRadius="2xl" 
          mb={{ base: 3, md: 6 }}
          boxShadow="0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          border="1px solid"
          borderColor={borderColor}
          overflow="hidden"
        >
          {/* Decorative gradient overlay - Blue theme for filters */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height="4px"
            bg="linear-gradient(90deg, #3B82F6, #2563EB, #1D4ED8)"
          />
          
          {/* Header section with icon and title */}
          <Flex align="center" mb={4} direction={{ base: 'column', md: 'row' }} textAlign={{base:'center',md:'left'}}>
            <Box
              p={3}
              bg="linear-gradient(135deg, #3B82F6, #2563EB)"
              borderRadius="lg"
              mr={4}
              boxShadow="0 4px 6px -1px rgba(59, 130, 246, 0.3)"
            >
              <Icon viewBox="0 0 24 24" boxSize={5} color="white">
                <path fill="currentColor" d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
              </Icon>
            </Box>
            <Box>
              <Text 
                fontSize="lg" 
                fontWeight="bold"
                bgGradient="linear(to-r, #3B82F6, #2563EB)"
                bgClip="text"
                mb={1}
              >
                Фільтр за періодом
              </Text>
              <Text 
                fontSize="sm" 
                color={subtextColor}
                fontWeight="medium"
              >
                Автоматичне оновлення даних
              </Text>
            </Box>
          </Flex>

          {/* Date inputs container */}
          <Flex gap={4} wrap="wrap" align="center" direction={{ base: 'column', md: 'row' }}>
            <Box>
              <Text fontSize="sm" color={subtextColor} mb={2} fontWeight="medium">
                Початкова дата
              </Text>
              <Box
                bg={listBg}
                p={3}
                borderRadius="lg"
                border="1px solid"
                borderColor={borderColor}
                _hover={{
                  borderColor: blueHoverBorder,
                  transition: "all 0.2s ease"
                }}
              >
                <Input 
                  type="date" 
                  size="sm" 
                  w="160px" 
                  value={startDate} 
                  onChange={e => setStartDate(e.target.value)}
                  border="none"
                  bg="transparent"
                  _focus={{
                    boxShadow: "none"
                  }}
                />
              </Box>
            </Box>
            
            <Box>
              <Text fontSize="sm" color={subtextColor} mb={2} fontWeight="medium">
                Кінцева дата
              </Text>
              <Box
                bg={listBg}
                p={3}
                borderRadius="lg"
                border="1px solid"
                borderColor={borderColor}
                _hover={{
                  borderColor: blueHoverBorder,
                  transition: "all 0.2s ease"
                }}
              >
                <Input 
                  type="date" 
                  size="sm" 
                  w="160px" 
                  value={endDate} 
                  onChange={e => setEndDate(e.target.value)}
                  border="none"
                  bg="transparent"
                  _focus={{
                    boxShadow: "none"
                  }}
                />
              </Box>
            </Box>

            {/* Auto-update indicator */}
            <Box ml={{ base: 0, md: 'auto' }} mt={{ base: 2, md: 0 }}>
              <Flex align="center" gap={2}>
                <Box
                  width="8px"
                  height="8px"
                  bg="green.400"
                  borderRadius="50%"
                  animation={`${pulse} 2s ease-in-out infinite`}
                />
                <Text fontSize="sm" color={subtextColor} fontWeight="medium">
                  Автооновлення
                </Text>
              </Flex>
            </Box>
          </Flex>
        </Box>

        <SimpleGrid columns={{ base: 1 }} spacing={5} mb={{ base: 3, md: 10 }}>
          <Box
            position="relative"
            overflow="hidden"
            bg="linear-gradient(135deg, #3B82F6 0%, #1E40AF 50%, #10B981 100%)"
            p={{ base: 4, md: 8 }}
            borderRadius="xl"
            animation={`${glow} 15s ease-in-out infinite`} // Slower animation
            transform={{ base: 'none', md: 'perspective(1000px) rotateX(0.5deg)' }} // Reduced 3D effect
            _hover={{
              transform: { base: 'none', md: 'perspective(1000px) rotateX(0deg) scale(1.005)' }, // Reduced scale
              transition: "all 0.3s ease"
            }}
            _before={{
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)", // Reduced intensity
              backgroundSize: "200% 100%",
              animation: `${shimmer} 12s infinite`, // Slower shimmer
              zIndex: 1,
            }}
          >
            {/* Simplified floating particles */}
            <Box
              position="absolute"
              top="15%"
              right="10%"
              width="8px" // Smaller
              height="8px"
              bg="rgba(255,255,255,0.15)" // More transparent
              borderRadius="50%"
              animation={`${pulse} 8s ease-in-out infinite`} // Slower
            />

            <Flex align="center" justify="center" direction="column" position="relative" zIndex={2}>
              <Stat textAlign="center" color="white">
                <StatLabel 
                  fontSize="xl" 
                  fontWeight="semibold" 
                  textShadow="0 2px 4px rgba(0,0,0,0.3)"
                  mb={4}
                  letterSpacing="wide"
                >
                  Всього зароблено
                </StatLabel>
                <StatNumber 
                  fontSize={{ base: "5xl", md: "7xl" }} 
                  fontWeight="bold"
                  textShadow="0 4px 8px rgba(0,0,0,0.3)"
                  background="linear-gradient(45deg, #ffffff, #e0f2fe)"
                  backgroundClip="text"
                  color="transparent"
                  animation={`${pulse} 12s ease-in-out infinite`} // Slower pulse
                  lineHeight="1"
                >
                  {(totalEarned || 0).toLocaleString()} грн
                </StatNumber>
              </Stat>
            </Flex>

            {/* Simplified corner accents */}
            <Box
              position="absolute"
              top={0}
              left={0}
              width="60px" // Smaller
              height="60px"
              background="linear-gradient(45deg, rgba(255,255,255,0.06), transparent)" // More subtle
              borderTopLeftRadius="xl"
            />
            <Box
              position="absolute"
              bottom={0}
              right={0}
              width="60px" // Smaller
              height="60px"
              background="linear-gradient(225deg, rgba(255,255,255,0.06), transparent)" // More subtle
              borderBottomRightRadius="xl"
            />
          </Box>
        </SimpleGrid>

        <Box 
          position="relative"
          bg={useColorModeValue('white', 'gray.800')} 
          p={{ base: 3, md: 8 }}
          borderRadius="2xl" 
          mb={{ base: 3, md: 10 }}
          boxShadow="0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          border="1px solid"
          borderColor={useColorModeValue('gray.200', 'gray.600')}
          _hover={{
            transform: { base: 'none', md: 'translateY(-2px)' },
            boxShadow: { base: 'none', md: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' },
            transition: "all 0.3s ease"
          }}
          overflow="hidden"
        >
          {/* Decorative gradient overlay */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height="4px"
            bg="linear-gradient(90deg, #8B5CF6, #6366F1, #3B82F6)"
          />
          
          {/* Header section with icon and title */}
          <Flex align="center" mb={6} direction={{ base: 'column', md: 'row' }} textAlign={{base:'center',md:'left'}}>
            <Box
              p={3}
              bg="linear-gradient(135deg, #8B5CF6, #6366F1)"
              borderRadius="lg"
              mr={4}
              boxShadow="0 4px 6px -1px rgba(139, 92, 246, 0.3)"
            >
              <Icon viewBox="0 0 24 24" boxSize={6} color="white">
                <path fill="currentColor" d="M3 13h2v8H3v-8zm4-6h2v14H7V7zm4-4h2v18h-2V3zm4 2h2v16h-2V5zm4 4h2v12h-2V9z"/>
              </Icon>
            </Box>
            <Box>
              <Text 
                fontSize="2xl" 
                fontWeight="bold"
                bgGradient="linear(to-r, #8B5CF6, #6366F1)"
                bgClip="text"
                mb={1}
              >
                Графік прибутку
              </Text>
              <Text 
                fontSize="sm" 
                color={useColorModeValue('gray.600', 'gray.400')}
                fontWeight="medium"
              >
                {startDate} - {endDate}
              </Text>
            </Box>
          </Flex>

          {/* Chart container with enhanced styling */}
          <Box 
            position="relative"
            bg={useColorModeValue('gray.50', 'gray.700')}
            borderRadius="xl"
            p={{ base: 2, md: 6 }}
            height={{ base: '250px', md: '400px' }}
            border="1px solid"
            borderColor={useColorModeValue('gray.200', 'gray.600')}
            overflowX={{ base: 'auto', md: 'visible' }}
          >
            <Bar data={chartConfig} options={chartOptions} />
          </Box>
          
          {/* Stats summary at the bottom */}
          <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4} mt={6}>
            <Box textAlign="center" p={3} bg={useColorModeValue('purple.50', 'purple.900')} borderRadius="lg">
              <Text fontSize="sm" color={useColorModeValue('purple.600', 'purple.300')} fontWeight="medium">
                Загальний прибуток
              </Text>
              <Text fontSize="lg" fontWeight="bold" color={useColorModeValue('purple.700', 'purple.200')}>
                {(totalEarned || 0).toLocaleString()} грн
              </Text>
            </Box>
            <Box textAlign="center" p={3} bg={useColorModeValue('blue.50', 'blue.900')} borderRadius="lg">
              <Text fontSize="sm" color={useColorModeValue('blue.600', 'blue.300')} fontWeight="medium">
                Період
              </Text>
              <Text fontSize="lg" fontWeight="bold" color={useColorModeValue('blue.700', 'blue.200')}>
                {chartLabels.length} днів
              </Text>
            </Box>
            <Box textAlign="center" p={3} bg={useColorModeValue('green.50', 'green.900')} borderRadius="lg">
              <Text fontSize="sm" color={useColorModeValue('green.600', 'green.300')} fontWeight="medium">
                Середній дохід
              </Text>
              <Text fontSize="lg" fontWeight="bold" color={useColorModeValue('green.700', 'green.200')}>
                {chartLabels.length > 0 ? Math.round(totalEarned / chartLabels.length).toLocaleString() : 0} грн
              </Text>
            </Box>
            <Box textAlign="center" p={3} bg={useColorModeValue('orange.50', 'orange.900')} borderRadius="lg">
              <Text fontSize="sm" color={useColorModeValue('orange.600', 'orange.300')} fontWeight="medium">
                Макс. день
              </Text>
              <Text fontSize="lg" fontWeight="bold" color={useColorModeValue('orange.700', 'orange.200')}>
                {Math.max(...chartData).toLocaleString()} грн
              </Text>
            </Box>
          </SimpleGrid>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 6 }}>
          {/* Top Selling Products - Modern Card Style */}
          <Box 
            position="relative"
            bg={cardBg} 
            p={{ base: 3, md: 8 }}
            borderRadius="2xl" 
            boxShadow="0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
            border="1px solid"
            borderColor={borderColor}
            _hover={{
              transform: { base: 'none', md: 'translateY(-2px)' },
              boxShadow: { base: 'none', md: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' },
              transition: 'all 0.3s ease',
            }}
            overflow="hidden"
            mb={{ base: 4, md: 0 }}
          >
            {/* Decorative gradient overlay - Green theme for top products */}
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              height="4px"
              bg="linear-gradient(90deg, #10B981, #059669, #047857)"
            />
            
            {/* Header section with icon and title */}
            <Flex align="center" mb={6}>
              <Box
                p={3}
                bg="linear-gradient(135deg, #10B981, #059669)"
                borderRadius="lg"
                mr={4}
                boxShadow="0 4px 6px -1px rgba(16, 185, 129, 0.3)"
              >
                <Icon viewBox="0 0 24 24" boxSize={6} color="white">
                  <path fill="currentColor" d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z"/>
                </Icon>
              </Box>
              <Box>
                <Text 
                  fontSize="xl" 
                  fontWeight="bold"
                  bgGradient="linear(to-r, #10B981, #059669)"
                  bgClip="text"
                  mb={1}
                >
                  Найбільш продавані продукти
                </Text>
                <Text 
                  fontSize="sm" 
                  color={useColorModeValue('gray.600', 'gray.400')}
                  fontWeight="medium"
                >
                  Топ товари за прибутком
                </Text>
              </Box>
            </Flex>

            {/* Products list container */}
            <Box 
              bg={listBg}
              borderRadius="xl"
              p={4}
              border="1px solid"
              borderColor={borderColor}
            >
              <VStack align="start" spacing={3}>
                {(topProducts || []).map((product, idx) => {
                  const isProductLong = (product.product || '').length > 18;
                  const isRevenueLong = (product.total_revenue || '').toString().length > 10;
                  const isSoldLong = (product.amount_sold || '').toString().length > 8;
                  const longValue = isProductLong ? product.product : isRevenueLong ? `${product.total_revenue} грн` : isSoldLong ? `Продано: ${product.amount_sold}` : null;
                  const handleShow = () => { if (longValue) setSelectedText(longValue); };
                  return (
                    <Box 
                      key={idx} 
                      w="100%" 
                      bg={itemBg}
                      p={4}
                      borderRadius="lg"
                      border="1px solid"
                      borderColor={itemBorderColor}
                      _hover={{
                        borderColor: greenHoverBorder,
                        transform: "translateX(4px)",
                        transition: "all 0.2s ease"
                      }}
                      overflowX={{ base: 'auto', md: 'visible' }}
                      maxW="100%"
                      minW={0}
                      tabIndex={longValue ? 0 : -1}
                      onClick={longValue ? handleShow : undefined}
                      style={longValue ? { cursor: 'pointer' } : {}}
                    >
                      <HStack justify="space-between" mb={2} wrap="nowrap" spacing={2}>
                        <Tooltip 
                          isDisabled={!isProductLong} 
                          label={product.product}
                          bg="rgba(30,30,35,0.98)" color="white" fontWeight="bold" fontSize="lg" placement="auto-end" hasArrow zIndex={10} shouldWrapChildren={false} pointerEvents="auto" p={2}>
                          <Text fontWeight="semibold" color={textColor} noOfLines={1} minW={0} maxW="65%" fontSize={{base:'sm',md:'md'}} isTruncated onClick={isProductLong ? () => setSelectedText(product.product) : undefined} style={isProductLong ? { cursor: 'pointer', textDecoration: 'underline dotted' } : {}}>
                            {product.product}
                          </Text>
                        </Tooltip>
                        <Tooltip 
                          isDisabled={!isRevenueLong} 
                          label={`${product.total_revenue} грн`} 
                          bg="rgba(30,30,35,0.98)" 
                          color="white" fontWeight="bold" fontSize="lg" placement="auto-end"
                          hasArrow 
                          zIndex={10}
                          shouldWrapChildren={false} 
                          pointerEvents="auto" p={2}>
                          <Text 
                            fontWeight="bold" 
                            color={greenTextColor}
                            fontSize={{ base: 'sm', md: 'lg' }}
                            whiteSpace="nowrap" 
                            overflow="hidden" 
                            textOverflow="ellipsis"
                            onClick={isRevenueLong ? () => setSelectedText(`${product.total_revenue} грн`) : undefined}
                            style={isRevenueLong ? { cursor: 'pointer', textDecoration: 'underline dotted' } : {}}>
                            {product.total_revenue} грн
                          </Text>
                        </Tooltip>
                      </HStack>
                      <Tooltip 
                        isDisabled={!isSoldLong} 
                        label={`Продано: ${product.amount_sold}`} 
                        bg="rgba(30,30,35,0.98)" 
                        color="white" fontWeight="bold" fontSize="lg" placement="auto-end" hasArrow zIndex={10} shouldWrapChildren={false} pointerEvents="auto" p={2}>
                        <Text 
                          fontSize="sm" 
                          color={subtextColor}
                          fontWeight="medium"
                          noOfLines={1}
                          minW={0}
                          maxW="100%"
                          isTruncated
                          onClick={isSoldLong ? () => setSelectedText(`Продано: ${product.amount_sold}`) : undefined}
                          style={isSoldLong ? { cursor: 'pointer', textDecoration: 'underline dotted' } : {}}>
                          Продано: {product.amount_sold}
                        </Text>
                      </Tooltip>
                    </Box>
                  );
                })}
              </VStack>
            </Box>
          </Box>

          {/* Least Selling Products - Modern Card Style */}
          <Box 
            position="relative"
            bg={cardBg} 
            p={{ base: 3, md: 8 }}
            borderRadius="2xl" 
            boxShadow="0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
            border="1px solid"
            borderColor={borderColor}
            _hover={{
              transform: { base: 'none', md: 'translateY(-2px)' },
              boxShadow: { base: 'none', md: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' },
              transition: 'all 0.3s ease',
            }}
            overflow="hidden"
          >
            {/* Decorative gradient overlay - Orange theme for least products */}
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              height="4px"
              bg="linear-gradient(90deg, #F59E0B, #D97706, #B45309)"
            />
            
            {/* Header section with icon and title */}
            <Flex align="center" mb={6}>
              <Box
                p={3}
                bg="linear-gradient(135deg, #F59E0B, #D97706)"
                borderRadius="lg"
                mr={4}
                boxShadow="0 4px 6px -1px rgba(245, 158, 11, 0.3)"
              >
                <Icon viewBox="0 0 24 24" boxSize={6} color="white">
                  <path fill="currentColor" d="M12 2L13.09 8.26L20 9L15 14L16.18 21L12 17.77L7.82 21L9 14L4 9L10.91 8.26L12 2Z" opacity="0.5"/>
                </Icon>
              </Box>
              <Box>
                <Text 
                  fontSize="xl" 
                  fontWeight="bold"
                  bgGradient="linear(to-r, #F59E0B, #D97706)"
                  bgClip="text"
                  mb={1}
                >
                  Найменш продавані продукти
                </Text>
                <Text 
                  fontSize="sm" 
                  color={useColorModeValue('gray.600', 'gray.400')}
                  fontWeight="medium"
                >
                  Товари що потребують уваги
                </Text>
              </Box>
            </Flex>

            {/* Products list container */}
            <Box 
              bg={listBg}
              borderRadius="xl"
              p={4}
              border="1px solid"
              borderColor={borderColor}
            >
              <VStack align="start" spacing={3}>
                {(lessProducts || []).map((product, idx) => {
                  const isProductLong = (product.product || '').length > 18;
                  const isRevenueLong = (product.total_revenue || '').toString().length > 10;
                  const isSoldLong = (product.amount_sold || '').toString().length > 8;
                  // Combine product values/triggers into one handler with textLabel for genericity.
                  const openBar = (text) => setSelectedText(text);
                  const longValue = isProductLong ? product.product : isRevenueLong ? `${product.total_revenue} грн` : isSoldLong ? `Продано: ${product.amount_sold}` : null;
                  const handleShow = () => { if (longValue) setSelectedText(longValue); };
                  return (
                    <Box 
                      key={idx} 
                      w="100%" 
                      bg={itemBg}
                      p={4}
                      borderRadius="lg"
                      border="1px solid"
                      borderColor={itemBorderColor}
                      _hover={{
                        borderColor: orangeHoverBorder,
                        transform: "translateX(4px)",
                        transition: "all 0.2s ease"
                      }}
                      overflowX={{ base: 'auto', md: 'visible' }}
                      maxW="100%"
                      minW={0}
                      tabIndex={longValue ? 0 : -1}
                      onMouseEnter={longValue ? handleShow : undefined}
                      onFocus={longValue ? handleShow : undefined}
                      onClick={longValue ? handleShow : undefined}
                      style={longValue ? { cursor: 'pointer' } : {}}
                    >
                      <HStack justify="space-between" mb={2} wrap="nowrap" spacing={2}>
                        <Tooltip 
                          isDisabled={!isProductLong} 
                          label={product.product}
                          bg="rgba(32,34,37,0.85)" color="white" minW="unset" fontSize="md" placement="right" hasArrow zIndex={10} shouldWrapChildren={false} pointerEvents="auto">
                          <Text fontWeight="semibold" color={textColor} noOfLines={1} minW={0} maxW="65%" fontSize={{base:'sm',md:'md'}} isTruncated onClick={isProductLong ? () => setSelectedText(product.product) : undefined} style={isProductLong ? { cursor: 'pointer', textDecoration: 'underline dotted' } : {}}>
                            {product.product}
                          </Text>
                        </Tooltip>
                        <Tooltip 
                          isDisabled={!isRevenueLong} 
                          label={`${product.total_revenue} грн`} 
                          bg="rgba(32,34,37,0.85)" 
                          color="white" 
                          fontSize="md" 
                          placement="right" 
                          hasArrow 
                          zIndex={10}
                          shouldWrapChildren={false} 
                          pointerEvents="auto">
                          <Text 
                            fontWeight="bold" 
                            color={orangeTextColor}
                            fontSize={{ base: 'sm', md: 'lg' }}
                            whiteSpace="nowrap" 
                            overflow="hidden" 
                            textOverflow="ellipsis"
                            onClick={isRevenueLong ? () => setSelectedText(`${product.total_revenue} грн`) : undefined}
                            style={isRevenueLong ? { cursor: 'pointer', textDecoration: 'underline dotted' } : {}}>
                            {product.total_revenue} грн
                          </Text>
                        </Tooltip>
                      </HStack>
                      <Tooltip 
                        isDisabled={!isSoldLong} 
                        label={`Продано: ${product.amount_sold}`} 
                        bg="rgba(32,34,37,0.85)" 
                        color="white" 
                        fontSize="md" 
                        placement="right" 
                        hasArrow 
                        zIndex={10}
                        shouldWrapChildren={false} 
                        pointerEvents="auto">
                        <Text 
                          fontSize="sm" 
                          color={subtextColor}
                          fontWeight="medium"
                          noOfLines={1}
                          minW={0}
                          maxW="100%"
                          isTruncated
                          onClick={isSoldLong ? () => setSelectedText(`Продано: ${product.amount_sold}`) : undefined}
                          style={isSoldLong ? { cursor: 'pointer', textDecoration: 'underline dotted' } : {}}>
                          Продано: {product.amount_sold}
                        </Text>
                      </Tooltip>
                    </Box>
                  );
                })}
              </VStack>
            </Box>
          </Box>
        </SimpleGrid>
      </Box>
      {/* Show Modal for full text display (shared for all sections) */}
      <Modal isOpen={!!selectedText} onClose={() => setSelectedText(null)} isCentered blockScrollOnMount={false} size="xl" autoFocus={false}>
        <ModalOverlay bg="transparent" />
        <ModalContent 
          bg="rgba(40,40,40,0.75)"
          color="white"
          borderRadius="lg"
          mx={{ base: 2, md: 'auto' }}
          boxShadow="0 8px 32px 0 rgba( 31, 38, 135, 0.21 )"
          p={0}
          my={12}
          pointerEvents="auto"
          style={{ bottom: 0, position: 'fixed', left: 0, right: 0, margin: '0 auto', width: 'fit-content', maxWidth: '95vw' }}
        >
          <ModalBody fontSize="lg" fontWeight="bold" p={6} textAlign="center" wordBreak="break-word">
            {selectedText}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Dashboard;